import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotify } from '../hooks';
import { printClient, newPrintIdempotencyKey } from '../utils/printClient';
import { readMsBarcodeFormat, readPrintAfterProduction, readStoredPrinter } from '../utils/printerState.mjs';
import TsdScanCapture from '../components/tsd/TsdScanCapture';
import RecentProductLabels from '../components/production/RecentProductLabels';
import { checkMaterialsRequestParams, isCrumbProduct, kgFromScale, matchOpenStagesByInputScan, mergeProductionTaskHeader, parseProductionQty, pickProcessTypeHref, pickWeightProcessType, processTypeRequiresWeight, productionProductLines, readStoredProcessTypeHref, resolveProductionNextWeightKg, shouldKeepNamedTask, writeStoredProcessTypeHref } from './productionUi.mjs';
import {
  ACTING_IDLE_MS,
  employeeDisplayName,
  isActingExpired,
  isStationUser,
  shouldBlockProduce,
} from './productionStation.mjs';

// Производство только по скану ШК ПФ. Вернуть кнопку на карточке — true.
const SHOW_STAGE_PRODUCE_BUTTON = false;

const PRODUCTION_CARD_GRID_SX = {
  display: 'grid',
  gap: { xs: 1.5, sm: 2 },
  alignItems: 'stretch',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
};

const CARD_TITLE_SX = {
  fontSize: '1rem',
  fontWeight: 'bold',
  lineHeight: 1.35,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

const CARD_SECONDARY_SX = {
  fontSize: '0.875rem',
  lineHeight: 1.4,
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
};

function formatWeight(weight, unit) {
  if (unit === 'g') return `${Math.round(weight)} г`;
  if (unit === 'kg') return `${Number(weight).toFixed(3)} кг`;
  return `${weight} ${unit || ''}`.trim();
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(String(dateStr).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return dateStr;
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return dateStr;
  }
}

export default function ProductionNext() {
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError, warning: showWarning } = useNotify();
  const authUser = useSelector((state) => state.auth.user);
  const station = isStationUser(authUser);

  const [acting, setActing] = useState(() => {
    if (!authUser || isStationUser(authUser)) return null;
    return {
      id: authUser.id,
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      name: `${authUser.lastName || ''} ${authUser.firstName || ''}`.trim(),
      ms_id: authUser.ms_id || null,
      employee_number: authUser.employee_number || String(authUser.id),
    };
  });
  const [types, setTypes] = useState([]);
  const [selectedTypeHref, setSelectedTypeHref] = useState('');
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState('list');
  const [currentTask, setCurrentTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState({ stages: [], products: [], loading: false });
  const [confirm, setConfirm] = useState({
    open: false,
    stageId: null,
    productName: '',
    productId: null,
    planId: null,
    colorLabel: '',
    quantity: '1',
    weightKg: 0,
    manualWeight: '',
  });
  const [pickOpen, setPickOpen] = useState(false);
  const [pickStages, setPickStages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialsMeta, setMaterialsMeta] = useState({ ok: true, quantity: 1 });
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [labelsRefresh, setLabelsRefresh] = useState(0);
  const [scaleData, setScaleData] = useState({ weight: 0, unit: 'kg', stable: false, timestamp: null });

  const submittingRef = useRef(false);
  const esRef = useRef(null);
  const touchAtRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const actingRef = useRef(null);

  const selectedType = useMemo(
    () => types.find((t) => t.href === selectedTypeHref) || null,
    [types, selectedTypeHref],
  );
  const weightType = useMemo(
    () => pickWeightProcessType(types, {
      selectedHref: selectedTypeHref,
      taskHref: currentTask?.processTypeHref,
      taskId: currentTask?.processTypeId,
    }),
    [types, selectedTypeHref, currentTask?.processTypeHref, currentTask?.processTypeId],
  );
  const requiresWeight = processTypeRequiresWeight(weightType);
  const typeId = selectedType?.id || weightType?.id || '';
  const confirmIsCrumb = isCrumbProduct(confirm.productId);
  const taskHasCrumb = (taskDetails.products || []).some((product) => isCrumbProduct(product?.productId));
  const listenScale = requiresWeight || taskHasCrumb || (confirm.open && confirmIsCrumb);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/ms/process-types');
        const list = data?.types || [];
        setTypes(list);
        setSelectedTypeHref((current) => pickProcessTypeHref(list, {
          storedHref: readStoredProcessTypeHref(),
          taskHref: current,
        }) || current);
      } catch (_) { /* типы подтянутся по кнопке */ }
    })();
  }, []);

  useEffect(() => {
    if (!listenScale) {
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      return undefined;
    }
    const token = localStorage.getItem('token');
    const base = axios.defaults.baseURL || (import.meta.env.VITE_API_URL || '');
    const url = `${base}/scale/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url, { withCredentials: false });
    esRef.current = es;
    es.addEventListener('update', (evt) => {
      try {
        setScaleData(JSON.parse(evt.data));
      } catch (_) { /* ignore */ }
    });
    es.addEventListener('ping', () => {});
    return () => {
      es.close();
      esRef.current = null;
    };
  }, [listenScale]);

  const bumpActivity = () => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (!station || !actingRef.current) return;
    if (now - touchAtRef.current < 15000) return;
    touchAtRef.current = now;
    axios.post('/ms/station/touch', {}, { silent: true })
      .then(({ data }) => {
        if (!data?.employee) setActing(null);
        else setActing(data.employee);
      })
      .catch(() => {});
  };

  useEffect(() => {
    actingRef.current = acting;
  }, [acting]);

  useEffect(() => {
    if (!station) {
      if (!authUser) {
        setActing(null);
        return;
      }
      setActing({
        id: authUser.id,
        firstName: authUser.firstName,
        lastName: authUser.lastName,
        name: `${authUser.lastName || ''} ${authUser.firstName || ''}`.trim(),
        ms_id: authUser.ms_id || null,
        employee_number: authUser.employee_number || String(authUser.id),
      });
      return;
    }
    let cancelled = false;
    axios.get('/ms/station/acting', { silent: true })
      .then(({ data }) => {
        if (!cancelled) setActing(data?.employee || null);
      })
      .catch(() => {
        if (!cancelled) setActing(null);
      });
    return () => { cancelled = true; };
  }, [station, authUser]);

  useEffect(() => {
    if (!station) return undefined;
    const onAct = () => bumpActivity();
    window.addEventListener('pointerdown', onAct);
    window.addEventListener('keydown', onAct);
    return () => {
      window.removeEventListener('pointerdown', onAct);
      window.removeEventListener('keydown', onAct);
    };
  }, [station]);

  useEffect(() => {
    if (!station || !acting) return undefined;
    const iv = setInterval(() => {
      if (isActingExpired(lastActivityRef.current, Date.now(), ACTING_IDLE_MS)) {
        setActing(null);
        axios.delete('/ms/station/acting', { silent: true }).catch(() => {});
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [station, acting]);

  const fetchTasks = async () => {
    if (!selectedTypeHref) return;
    try {
      setLoading(true);
      const { data } = await axios.get('/ms/production-tasks/list', { params: { typeHref: selectedTypeHref } });
      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch (_) {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const reloadDetails = async (taskId) => {
    try {
      setTaskDetails((s) => ({ ...s, loading: true }));
      const { data } = await axios.get(`/ms/production-tasks/${taskId}/details`);
      const stages = (data?.stages || []).filter((s) => Number(s.totalQuantity) !== Number(s.completedQuantity));
      setTaskDetails({ stages, products: data?.products || [], loading: false });
      setCurrentTask((prev) => mergeProductionTaskHeader(prev, {
        id: data?.id || taskId,
        name: data?.name,
        moment: data?.moment,
        description: data?.description,
        href: data?.href,
        processTypeId: data?.processTypeId || null,
        processTypeHref: data?.processTypeHref || null,
      }, taskId));
      if (data?.processTypeHref) {
        setSelectedTypeHref(data.processTypeHref);
        writeStoredProcessTypeHref(data.processTypeHref);
      }
    } catch (_) {
      setTaskDetails({ stages: [], products: [], loading: false });
    }
  };

  const openTask = async (task) => {
    setCurrentTask(mergeProductionTaskHeader(null, task, task?.id));
    setView('details');
    try {
      const params = new URLSearchParams(location.search);
      params.set('task', task.id);
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) { /* ignore */ }
    await reloadDetails(task.id);
  };

  const backToList = async () => {
    setView('list');
    setCurrentTask(null);
    setTaskDetails({ stages: [], products: [], loading: false });
    try {
      const params = new URLSearchParams(location.search);
      params.delete('task');
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) { /* ignore */ }
    await fetchTasks();
  };

  const productForStage = (stage) => (taskDetails.products || []).find((p) => p.productionRowHref === stage.productionRowHref);

  const openConfirm = (stage, product) => {
    bumpActivity();
    if (shouldBlockProduce(station, acting)) {
      showWarning('Просканируйте бейдж');
      return;
    }
    setConfirm({
      open: true,
      stageId: stage.id,
      productName: product?.productName || '',
      productId: product?.productId || null,
      planId: product?.planId || stage.planId || null,
      colorLabel: product?.colorLabel || '',
      quantity: '1',
      weightKg: kgFromScale(scaleData),
      manualWeight: '',
    });
  };

  const closeConfirm = () => {
    if (submittingRef.current) return;
    setConfirm((s) => ({ ...s, open: false }));
  };

  const onCheckMaterials = async (stageRow, productRow) => {
    try {
      if (!productRow?.planId) return;
      setMaterialsOpen(true);
      setMaterialsLoading(true);
      setMaterials([]);
      const qty = Math.max(1, Number(stageRow.totalQuantity || 0) - Number(stageRow.completedQuantity || 0));
      const { data } = await axios.get('/ms/vacuum/check-materials', {
        params: checkMaterialsRequestParams({
          planId: productRow.planId,
          quantity: qty || 1,
          product: productRow,
        }),
      });
      setMaterials(Array.isArray(data?.items) ? data.items : []);
      setMaterialsMeta({ ok: Boolean(data?.ok), quantity: Number(data?.quantity || qty) });
    } catch (e) {
      setMaterials([]);
      setMaterialsMeta({ ok: false, quantity: 1 });
      setMaterialsOpen(true);
      showError(e?.response?.data?.message || 'Ошибка проверки материалов');
    } finally {
      setMaterialsLoading(false);
    }
  };

  // Параметры печати ШК для complete-stage: сервер ставит этикетку в очередь сам,
  // сразу после подтверждения МС (без отдельного запроса с ТСД).
  const buildCompletionPrint = (qty) => {
    if (!readPrintAfterProduction()) return null;
    const stored = readStoredPrinter();
    if (!stored?.printer_uid || !confirm.productId) return null;
    return {
      printer_uid: stored.printer_uid,
      copies: qty,
      product_name: confirm.productName,
      color_label: confirm.colorLabel,
      label_format: readMsBarcodeFormat(),
      idempotency_key: newPrintIdempotencyKey('ms-barcode'),
    };
  };

  const maybePrintBarcode = async (qty, serverPrint) => {
    const enabled = readPrintAfterProduction();
    if (!enabled) return { printed: false };
    if (serverPrint) {
      if (serverPrint.ok) {
        setLabelsRefresh((n) => n + 1);
        return { printed: true };
      }
      return {
        printed: false,
        warning: `Этап выполнен, но печать не удалась: ${serverPrint.error?.message || 'ошибка принтера'}`,
      };
    }
    const stored = readStoredPrinter();
    if (!stored?.printer_uid) {
      return { printed: false, warning: 'Этап выполнен. Выберите принтер в Настройки → Печать' };
    }
    try {
      await printClient.createMsProductBarcode({
        printerUid: stored.printer_uid,
        productId: confirm.productId,
        copies: qty,
        productName: confirm.productName,
        colorLabel: confirm.colorLabel,
        labelFormat: readMsBarcodeFormat(),
        idempotencyKey: newPrintIdempotencyKey('ms-barcode'),
      });
      setLabelsRefresh((n) => n + 1);
      return { printed: true };
    } catch (e) {
      return {
        printed: false,
        warning: `Этап выполнен, но печать не удалась: ${e?.response?.data?.message || e.message || 'ошибка принтера'}`,
      };
    }
  };

  const completeStage = async () => {
    if (submittingRef.current || !confirm.stageId) return;
    bumpActivity();
    if (shouldBlockProduce(station, acting)) {
      showWarning('Просканируйте бейдж');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const quantity = parseProductionQty(confirm.quantity);
      const payload = {
        stageId: confirm.stageId,
        processTypeId: typeId || undefined,
        processTypeHref: selectedTypeHref || undefined,
        quantity,
        productId: confirm.productId || undefined,
        planId: confirm.planId || undefined,
      };
      const sendWeight = confirmIsCrumb || requiresWeight;
      if (sendWeight) {
        payload.weightKg = resolveProductionNextWeightKg({
          productId: confirm.productId,
          scaleKg: kgFromScale(scaleData),
          snapshotKg: confirm.weightKg,
          manualWeight: confirm.manualWeight,
        });
      }
      const print = buildCompletionPrint(quantity);
      if (print) payload.print = print;
      const { data } = await axios.post('/ms/complete-stage', payload);
      const printResult = await maybePrintBarcode(quantity, data?.print || null);
      if (printResult.warning) showWarning(printResult.warning);
      else showSuccess(printResult.printed ? 'Этап выполнен, этикетка отправлена на печать' : 'Этап выполнен');
      setConfirm((s) => ({ ...s, open: false }));
      if (currentTask?.id) await reloadDetails(currentTask.id);
    } catch (e) {
      showError(e?.response?.data?.message || e.message || 'Ошибка выполнения этапа');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleScan = async (raw) => {
    const code = String(raw || '').trim();
    if (!code) return;
    if (confirm.open || pickOpen || materialsOpen || labelsOpen) return;
    bumpActivity();
    if (station) {
      try {
        const { data } = await axios.post('/ms/station/scan', { code });
        if (data?.matched && data.employee) {
          setActing(data.employee);
          lastActivityRef.current = Date.now();
          showSuccess(employeeDisplayName(data.employee) || 'Сотрудник выбран');
          return;
        }
      } catch (e) {
        showError(e?.response?.data?.message || 'Не удалось распознать бейдж');
        return;
      }
    }
    if (view !== 'details') return;
    try {
      const { data } = await axios.get('/ms/resolve-barcode', { params: { code } });
      const matches = matchOpenStagesByInputScan(taskDetails.stages, data);
      if (!matches.length) {
        showError('В этом задании нет этапа для этого ПФ');
        return;
      }
      if (matches.length === 1) {
        openConfirm(matches[0], productForStage(matches[0]));
        return;
      }
      setPickStages(matches);
      setPickOpen(true);
    } catch (e) {
      showError(e?.response?.status === 404
        ? 'ШК не найден'
        : (e?.response?.data?.message || 'Не удалось распознать штрихкод'));
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (!taskId) return;
    const alreadyOpen = view === 'details' && String(currentTask?.id || '') === String(taskId);
    const fromList = (tasks || []).find((t) => String(t.id) === String(taskId));
    setCurrentTask((prev) => {
      if (shouldKeepNamedTask(prev, taskId)) return mergeProductionTaskHeader(prev, fromList || {}, taskId);
      if (fromList) return mergeProductionTaskHeader(prev, fromList, taskId);
      if (prev && String(prev.id) === String(taskId)) return prev;
      return { id: taskId };
    });
    if (!alreadyOpen) {
      setView('details');
      reloadDetails(taskId);
    }
  }, [location.search]);

  const typeFilter = (
    <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 200, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
        <InputLabel>Тип производства</InputLabel>
        <Select
          label="Тип производства"
          value={selectedTypeHref}
          onChange={(e) => {
            setSelectedTypeHref(e.target.value);
            writeStoredProcessTypeHref(e.target.value);
          }}
        >
          {!selectedTypeHref ? <MenuItem value="">Выберите тип</MenuItem> : null}
          {types.map((t) => (
            <MenuItem key={t.id} value={t.href}>{t.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="contained"
        onClick={fetchTasks}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {loading ? 'Загрузка…' : 'Получить задания'}
      </Button>
    </Box>
  );

  const renderStageCard = (st) => {
    const prod = productForStage(st);
    const productLines = productionProductLines(prod);
    return (
      <Card
        key={st.id}
        variant="outlined"
        onClick={() => openConfirm(st, prod)}
        sx={{
          height: '100%',
          minHeight: { xs: 168, sm: 188 },
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            gap: 0.75,
            p: { xs: 1.5, sm: 2 },
            minHeight: { xs: 168, sm: 188 },
            '&:last-child': { pb: { xs: 1.5, sm: 2 } },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: '0 0 auto' }}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                onCheckMaterials(st, prod);
              }}
              sx={{ cursor: 'pointer', flex: '1 1 auto', minWidth: 0 }}
            >
              <Typography variant="h6" sx={CARD_TITLE_SX}>
                {productLines.name}
              </Typography>
              {productLines.color ? (
                <Typography variant="body2" color="text.secondary" sx={{ ...CARD_SECONDARY_SX, mt: 0.5 }}>
                  {productLines.color}
                </Typography>
              ) : null}
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexShrink: 0, fontSize: '0.875rem', lineHeight: 1.35, pt: 0.25, whiteSpace: 'nowrap' }}
            >
              {st.totalQuantity} / {st.completedQuantity}
            </Typography>
          </Box>
          <Box aria-hidden sx={{ flex: '1 1 auto' }} />
          {SHOW_STAGE_PRODUCE_BUTTON ? (
            <Button
              size="small"
              variant="contained"
              fullWidth
              disabled={submitting}
              onClick={(e) => {
                e.stopPropagation();
                openConfirm(st, prod);
              }}
            >
              Произвести
            </Button>
          ) : null}
        </CardContent>
      </Card>
    );
  };

  const renderTaskCard = (t) => (
    <Card
      key={t.id}
      variant="outlined"
      onClick={() => openTask(t)}
      sx={{
        height: '100%',
        minHeight: { xs: 168, sm: 188 },
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: 0.75,
          p: { xs: 1.5, sm: 2 },
          minHeight: { xs: 168, sm: 188 },
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
        }}
      >
        <Typography variant="h6" sx={CARD_TITLE_SX}>
          {t.name || t.id}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={CARD_SECONDARY_SX}>
          {formatDate(t.moment)}
        </Typography>
        {t.description ? (
          <Typography variant="body2" color="text.secondary" sx={CARD_SECONDARY_SX}>
            {t.description}
          </Typography>
        ) : null}
        <Box aria-hidden sx={{ flex: '1 1 auto' }} />
      </CardContent>
    </Card>
  );

  const needBadge = station && !acting;
  const actingName = employeeDisplayName(acting);

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper sx={{
        p: 1.25,
        px: 1.5,
        mb: 1,
        bgcolor: actingName ? 'success.light' : 'warning.light',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.2, letterSpacing: 0.6 }}>
              Сотрудник
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {actingName || 'Просканируйте бейдж'}
            </Typography>
          </Box>
          {listenScale ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.25rem' },
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  letterSpacing: '-0.03em',
                }}
              >
                {formatWeight(scaleData.weight, scaleData.unit)}
              </Typography>
              <Chip label={scaleData.stable ? 'Стабильно' : 'Нестабильно'} color={scaleData.stable ? 'success' : 'warning'} />
            </Box>
          ) : null}
          {!needBadge ? (
            <TsdScanCapture
              compact
              pause={confirm.open || pickOpen || materialsOpen || labelsOpen}
              onScan={handleScan}
              label="Ввести ШК вручную"
              dialogTitle={view === 'details' ? 'Ручной ввод бейджа или ШК' : 'Ручной ввод бейджа'}
              dialogPlaceholder={view === 'details' ? 'Бейдж или ШК полуфабриката' : 'Бейдж'}
            />
          ) : null}
        </Box>
      </Paper>

      {view === 'list' && (
        <>
          <Paper sx={{ p: 1.5, mb: 1 }}>{typeFilter}</Paper>
          <Paper sx={{ p: 1.5 }}>
            {tasks.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                {loading ? 'Загрузка...' : 'Нет заданий. Выберите тип и нажмите «Получить задания»'}
              </Typography>
            ) : (
              <Box sx={PRODUCTION_CARD_GRID_SX}>
                {tasks.map(renderTaskCard)}
              </Box>
            )}
          </Paper>
        </>
      )}

      {view === 'details' && (
        <Paper sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ ...CARD_TITLE_SX, mb: 0.25 }}>
                {currentTask?.name || currentTask?.id}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ...CARD_SECONDARY_SX }}>
                {formatDate(currentTask?.moment)}
                {currentTask?.description ? ` · ${currentTask.description}` : ''}
              </Typography>
            </Box>
            <Button variant="outlined" size="small" onClick={backToList} sx={{ flexShrink: 0 }}>Назад</Button>
          </Box>

          {taskDetails.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : taskDetails.stages.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>Все этапы выполнены</Typography>
          ) : (
            <Box sx={PRODUCTION_CARD_GRID_SX}>
              {taskDetails.stages.map(renderStageCard)}
            </Box>
          )}
        </Paper>
      )}

      <Dialog open={confirm.open} onClose={submitting ? undefined : closeConfirm}>
        <DialogTitle>Выполнить этап?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Товар: {confirm.productName || '—'}</Typography>
          {confirmIsCrumb ? (
            <>
              <Typography sx={{ mt: 1 }}>Вес (фикс.): {Number(confirm.weightKg || 0).toFixed(3)} кг</Typography>
              <TextField
                margin="dense"
                label="Вес вручную, кг"
                type="number"
                fullWidth
                value={confirm.manualWeight}
                onChange={(e) => setConfirm((s) => ({ ...s, manualWeight: e.target.value }))}
                placeholder="Например, 1.234"
                inputProps={{ step: '0.001', min: '0' }}
              />
            </>
          ) : requiresWeight ? (
            <Typography sx={{ mt: 1 }}>Вес с весов: {kgFromScale(scaleData).toFixed(3)} кг</Typography>
          ) : null}
          <TextField
            sx={{ mt: 2 }}
            label="Количество"
            fullWidth
            value={confirm.quantity}
            onChange={(e) => setConfirm((s) => ({ ...s, quantity: e.target.value }))}
            inputProps={{ inputMode: 'numeric' }}
            helperText="Пустое или нечисло при подтверждении = 1"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={submitting}>Нет</Button>
          <Button
            variant="contained"
            disabled={submitting || shouldBlockProduce(station, acting)}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            onClick={completeStage}
          >
            {submitting ? 'Выполняем…' : 'Да'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickOpen} onClose={() => setPickOpen(false)}>
        <DialogTitle>Несколько этапов для этого ПФ</DialogTitle>
        <DialogContent>
          <List>
            {pickStages.map((st) => {
              const prod = productForStage(st);
              const lines = productionProductLines(prod);
              return (
                <ListItemButton
                  key={st.id}
                  onClick={() => {
                    setPickOpen(false);
                    openConfirm(st, prod);
                  }}
                >
                  <ListItemText
                    primary={lines.name || prod?.productName || st.id}
                    secondary={lines.color || `${st.totalQuantity} / ${st.completedQuantity}`}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickOpen(false)}>Отмена</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={materialsOpen} onClose={() => setMaterialsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Проверка материалов (qty: {materialsMeta.quantity})</DialogTitle>
        <DialogContent dividers>
          {materialsLoading ? (
            <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Материал</TableCell>
                  <TableCell align="right">Нужно</TableCell>
                  <TableCell align="right">Есть</TableCell>
                  <TableCell align="right">Дефицит</TableCell>
                  <TableCell>Группа</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map((m, idx) => (
                  <TableRow key={idx} sx={{ bgcolor: m.shortage > 0 ? 'error.lighter' : undefined }}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell align="right">{m.need}</TableCell>
                    <TableCell align="right">{m.inStock}</TableCell>
                    <TableCell align="right" sx={{ color: m.shortage > 0 ? 'error.main' : undefined }}>{m.shortage}</TableCell>
                    <TableCell>{m.groupName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color={materialsMeta.ok ? 'success.main' : 'error.main'}>
              {materialsMeta.ok ? 'Материалов достаточно' : 'Есть дефицит материалов'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaterialsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <RecentProductLabels
        open={labelsOpen}
        onOpenChange={setLabelsOpen}
        refreshKey={labelsRefresh}
        onPrinted={() => showSuccess('Этикетка отправлена на печать')}
        onError={showError}
      />

      <Dialog open={needBadge} disableEscapeKeyDown disableAutoFocus disableEnforceFocus>
        <DialogTitle>Просканируйте бейдж</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, mb: 2 }}>
            Чтобы выполнять этапы, просканируйте QR-бейдж сотрудника.
          </Typography>
          <TsdScanCapture
            compact
            onScan={handleScan}
            label="Ввести бейдж вручную"
            dialogTitle="Ручной ввод бейджа"
            dialogPlaceholder="Номер бейджа"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
